import { isDocContent } from "@/components/shapes/doc-frame/doc-content";
import type { FrameData } from "@/lib/frame-data";
import { previewSource } from "@/lib/frame-preview-source";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

/**
 * Match the title shown by a Markdown frame when its body has a heading.
 * Docs (and V4 notes) use the same precedence on their markdown snapshot,
 * then the explicit title, then the first line of their text. Reads the
 * preview source, so a frame known only by its card has the same title.
 */
export function frameDisplayTitle(frame: FrameData): string {
  const explicitTitle = frame.title.trim();
  const content = previewSource(frame);
  const isDoc = frame.frameType === "doc" || isDocContent(content);
  if (isDoc || frame.frameType === "markdown" || frame.frameType === "note") {
    const markdown = content.markdown;
    if (typeof markdown === "string") {
      const heading = markdown.match(/^#{1,2}\s+(.+)$/m)?.[1]?.trim();
      if (heading) return heading;
    }
  }
  if (explicitTitle) return explicitTitle;
  const text = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";
  if (isDoc) {
    // A Doc card carries markdown only; its first line stands in for the text.
    const firstLine = (text(content.text) || text(content.markdown))
      .split("\n")[0]
      ?.replace(/^#+\s*/, "")
      .trim();
    if (firstLine) return firstLine;
  }
  const contentTitle = text(content.title);
  if (contentTitle) return contentTitle;
  if (frame.frameType === "weather") {
    const place = text(content.place);
    if (place) return translate(libNoticesMessages, "weatherIn", { place });
  }
  const fieldByType: Record<string, string> = {
    email: "subject",
    newsletter: "subject",
    file: "name",
    music: "track",
    link_preview: "url",
    search: "query",
    slack: "channel",
  };
  const field = fieldByType[frame.frameType];
  if (field) {
    const value = text(content[field]);
    if (value) return value;
  }
  // The card keeps a page's text, not its markup: the heading needs the page.
  if (frame.frameType === "html" && frame.contentState !== "card") {
    const html = text(frame.content.html);
    const heading = html.match(
      /<(?:title|h1)\b[^>]*>([^<]+)<\/(?:title|h1)>/i,
    )?.[1];
    if (heading?.trim()) return heading.trim();
  }
  return `${frame.frameType.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase())} frame`;
}
